(() => {
  if (!customElements.get('ctd-cart-discount')) {
    class CartDiscount extends HTMLElement {
      static feedback = null;
      static pending = false;
      connectedCallback() {
        this.controller = new AbortController();
        this.form = this.querySelector('form');
        this.input = this.querySelector('input');
        this.status = this.querySelector('[role="status"]');
        this.form.addEventListener('submit', event => {
          event.preventDefault();
          const code = this.input.value.trim();
          if (code) this.update(code);
        }, { signal: this.controller.signal });
        this.addEventListener('click', event => {
          const button = event.target.closest('[data-remove-code]');
          if (button) this.update(button.dataset.removeCode, true);
        }, { signal: this.controller.signal });
        this.input.addEventListener('input', () => {
          this.input.removeAttribute('aria-invalid');
          this.clearFeedback();
        }, { signal: this.controller.signal });
        this.showFeedback();
        this.setBusy(CartDiscount.pending);
        fetch(`${Shopify.routes.root}cart.js`, { signal: this.controller.signal, cache: 'no-store' })
          .then(response => response.ok ? response.json() : null)
          .then(cart => { if (cart && this.isConnected) this.renderCodes(cart); })
          .catch(() => {});
      }
      disconnectedCallback() { this.controller.abort(); clearTimeout(this.feedbackTimer); }
      clearFeedback() {
        clearTimeout(this.feedbackTimer);
        CartDiscount.feedback = null;
        this.status.hidden = true;
        this.status.textContent = '';
        this.input.removeAttribute('aria-invalid');
      }
      setBusy(busy) {
        this.setAttribute('aria-busy', String(busy));
        this.querySelectorAll('button, input').forEach(element => { element.disabled = busy; });
      }
      showFeedback() {
        const feedback = CartDiscount.feedback;
        clearTimeout(this.feedbackTimer);
        if (!feedback) return;
        feedback.expiresAt ??= Date.now() + 5000;
        const remaining = feedback.expiresAt - Date.now();
        if (remaining <= 0) { this.clearFeedback(); return; }
        this.status.textContent = this.dataset[feedback.key];
        this.status.dataset.error = String(feedback.error);
        this.status.hidden = false;
        if (feedback.error) this.input.setAttribute('aria-invalid', 'true');
        this.feedbackTimer = setTimeout(() => this.clearFeedback(), remaining);
      }
      renderCodes(cart) {
        const buttons = (cart.discount_codes || []).filter(item => item.applicable).map(item => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'ctd-cart-discount__code';
          button.dataset.removeCode = item.code;
          button.setAttribute('aria-label', this.dataset.removeLabel.replace('[code]', item.code));
          button.textContent = `${item.code} ×`;
          button.disabled = CartDiscount.pending;
          return button;
        });
        this.querySelector('.ctd-cart-discount__codes').replaceChildren(...buttons);
      }
      async update(code, remove = false) {
        if (CartDiscount.pending) return;
        CartDiscount.pending = true;
        this.setBusy(true);
        try {
          const currentResponse = await fetch(`${Shopify.routes.root}cart.js`, { cache: 'no-store' });
          if (!currentResponse.ok) throw new Error();
          const current = await currentResponse.json();
          const active = (current.discount_codes || []).filter(item => item.applicable).map(item => item.code);
          const codes = active.filter(item => item.toLowerCase() !== code.toLowerCase());
          if (!remove) codes.push(code);
          const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ discount: codes.join(',') })
          });
          if (!response.ok) throw new Error();
          const cart = await response.json();
          const applied = (cart.discount_codes || []).some(item => item.applicable && item.code.toLowerCase() === code.toLowerCase());
          CartDiscount.feedback = { key: remove ? 'removed' : applied ? 'success' : 'invalid', error: !remove && !applied };
          if (remove || applied) this.input.value = '';
          this.showFeedback();
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        } catch (error) {
          CartDiscount.feedback = { key: 'error', error: true };
          this.showFeedback();
        } finally {
          CartDiscount.pending = false;
          document.querySelectorAll('ctd-cart-discount').forEach(element => element.setBusy(false));
        }
      }
    }
    customElements.define('ctd-cart-discount', CartDiscount);
  }
  if (!customElements.get('ctd-cart-routine')) {
    class CartRoutine extends HTMLElement {
      static cache = new Map();
      connectedCallback() {
        this.controller = new AbortController();
        this.loading = false;
        this.track = this.querySelector('.ctd-cart-routine__track');
        this.buttons = [...this.querySelectorAll('[data-direction]')];
        this.index = 0;
        this.buttons.forEach(button => button.addEventListener('click', () => {
          this.goTo(this.index + Number(button.dataset.direction));
        }, { signal: this.controller.signal }));
        this.track.addEventListener('keydown', event => {
          if (event.target !== this.track) return;
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          if (event.key === 'Home') this.goTo(0);
          else if (event.key === 'End') this.goTo(this.track.children.length - 1);
          else this.goTo(this.index + (event.key === 'ArrowLeft' ? -1 : 1));
        }, { signal: this.controller.signal });
        this.track.addEventListener('pointerdown', event => this.startSwipe(event), { signal: this.controller.signal });
        this.track.addEventListener('pointermove', event => this.moveSwipe(event), { passive: false, signal: this.controller.signal });
        this.track.addEventListener('pointerup', event => this.endSwipe(event), { signal: this.controller.signal });
        this.track.addEventListener('pointercancel', event => this.endSwipe(event, true), { signal: this.controller.signal });
        this.track.addEventListener('dragstart', event => event.preventDefault(), { signal: this.controller.signal });
        this.track.addEventListener('click', event => {
          if (Date.now() < (this.suppressClickUntil || 0)) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        }, { capture: true, signal: this.controller.signal });
        const drawer = this.closest('cart-drawer');
        if (drawer?.hasAttribute('open') || Shopify.designMode) this.load();
        else drawer?.addEventListener('dialog:before-show', () => this.load(), { once: true, signal: this.controller.signal });
      }
      disconnectedCallback() { this.controller.abort(); this.swipe = null; }
      goTo(index) {
        this.index = Math.max(0, Math.min(index, this.track.children.length - 1));
        this.track.style.setProperty('--ctd-slide-index', this.index);
        this.track.style.removeProperty('--ctd-drag-offset');
        this.track.classList.remove('is-dragging');
        [...this.track.children].forEach((slide, position) => {
          slide.inert = position !== this.index;
          slide.setAttribute('aria-hidden', String(position !== this.index));
          slide.setAttribute('role', 'group');
          slide.setAttribute('aria-roledescription', 'slide');
          slide.setAttribute('aria-label', `${position + 1} / ${this.track.children.length}`);
        });
        this.updateNavigation();
      }
      updateNavigation() {
        this.buttons[0].disabled = this.index <= 0;
        this.buttons[1].disabled = this.index >= this.track.children.length - 1;
      }
      startSwipe(event) {
        if (!event.isPrimary || event.button !== 0 || event.target.closest('button, input, select, textarea')) return;
        this.swipe = { id: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now(), dragging: false };
      }
      moveSwipe(event) {
        const swipe = this.swipe;
        if (!swipe || swipe.id !== event.pointerId) return;
        const dx = event.clientX - swipe.x;
        const dy = event.clientY - swipe.y;
        if (!swipe.dragging) {
          if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) { this.swipe = null; return; }
          if (Math.abs(dx) < 8) return;
          swipe.dragging = true;
          this.track.setPointerCapture(event.pointerId);
          this.track.classList.add('is-dragging');
        }
        event.preventDefault();
        const atEdge = (this.index === 0 && dx > 0) || (this.index === this.track.children.length - 1 && dx < 0);
        const distance = atEdge ? dx * 0.18 : Math.max(-this.track.clientWidth, Math.min(dx, this.track.clientWidth));
        this.track.style.setProperty('--ctd-drag-offset', `${distance}px`);
      }
      endSwipe(event, cancelled = false) {
        const swipe = this.swipe;
        if (!swipe || swipe.id !== event.pointerId) return;
        this.swipe = null;
        if (!swipe.dragging) return;
        const dx = event.clientX - swipe.x;
        const speed = Math.abs(dx) / Math.max(1, performance.now() - swipe.time);
        const advance = !cancelled && (Math.abs(dx) > Math.max(36, this.track.clientWidth * 0.16) || (Math.abs(dx) > 12 && speed > 0.45));
        this.suppressClickUntil = Date.now() + 350;
        if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
        this.goTo(this.index + (advance ? (dx < 0 ? 1 : -1) : 0));
      }
      async recommendations(id) {
        const url = new URL(this.dataset.url, location.origin);
        url.searchParams.set('section_id', 'ctd-cart-recommendations');
        url.searchParams.set('product_id', id);
        url.searchParams.set('intent', 'related');
        url.searchParams.set('limit', '10');
        const key = url.href;
        const cached = CartRoutine.cache.get(key);
        if (cached && Date.now() - cached.time < 60000) return cached.html;
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error();
        const html = await response.text();
        CartRoutine.cache.set(key, { html, time: Date.now() });
        return html;
      }
      async load() {
        if (this.loading) return;
        this.loading = true;
        const generation = this.controller;
        const seeds = [...new Set(this.dataset.seeds.split(',').filter(Boolean))].reverse().slice(0, 3);
        if (!seeds.length) return;
        const seen = new Set(this.dataset.cartIds.split(','));
        const results = await Promise.allSettled(seeds.map(id => this.recommendations(id)));
        if (!this.isConnected || generation !== this.controller) return;
        const slides = [];
        for (const result of results) {
          if (result.status !== 'fulfilled') continue;
          const doc = new DOMParser().parseFromString(result.value, 'text/html');
          for (const slide of doc.querySelectorAll('[data-cart-routine-results] [data-product-id]')) {
            if (seen.has(slide.dataset.productId)) continue;
            seen.add(slide.dataset.productId);
            slides.push(slide);
            if (slides.length === 8) break;
          }
          if (slides.length === 8) break;
        }
        this.track.replaceChildren(...slides.map(slide => document.importNode(slide, true)));
        this.hidden = slides.length === 0;
        this.querySelector('.ctd-cart-routine__navigation').hidden = slides.length <= 1;
        this.goTo(0);
        this.loading = false;
      }
    }
    customElements.define('ctd-cart-routine', CartRoutine);
  }
})();
